import { create } from 'zustand'

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface Chat {
  id: string
  title: string
  messages: Message[]
  createdAt: number
  updatedAt: number
}

export interface Model {
  id: string
  name: string
}

interface ChatStore {
  chats: Chat[]
  currentChatId: string | null
  models: Model[]
  selectedModel: string
  streaming: boolean
  sidebarOpen: boolean
  showImageGen: boolean

  setSidebarOpen: (open: boolean) => void
  setShowImageGen: (show: boolean) => void
  setModels: (models: Model[]) => void
  setSelectedModel: (id: string) => void
  setStreaming: (s: boolean) => void

  createChat: () => string
  deleteChat: (id: string) => void
  selectChat: (id: string | null) => void
  renameChat: (id: string, title: string) => void

  addMessage: (message: Message) => void
  updateLastAssistantMessage: (content: string) => void

  setChats: (chats: Chat[]) => void
}

let _idCounter = 0
function genId(): string {
  return `${Date.now()}-${++_idCounter}-${Math.random().toString(36).slice(2, 8)}`
}

export const useChatStore = create<ChatStore>((set) => ({
  chats: [],
  currentChatId: null,
  models: [
    { id: 'nousresearch/hermes-4-70b', name: 'Hermes 4 70B' },
    { id: 'sao10k/l3.3-euryale-70b', name: 'Euryale 70B' },
  ],
  selectedModel: 'nousresearch/hermes-4-70b',
  streaming: false,
  sidebarOpen: true,
  showImageGen: false,

  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setShowImageGen: (show) => set({ showImageGen: show }),
  setModels: (models) => set({ models }),
  setSelectedModel: (id) => set({ selectedModel: id }),
  setStreaming: (s) => set({ streaming: s }),

  createChat: () => {
    const id = genId()
    set((s) => ({
      chats: [{ id, title: 'New Chat', messages: [], createdAt: Date.now(), updatedAt: Date.now() }, ...s.chats],
      currentChatId: id,
    }))
    return id
  },

  deleteChat: (id) => set((s) => {
    const chats = s.chats.filter((c) => c.id !== id)
    return { chats, currentChatId: s.currentChatId === id ? (chats[0]?.id ?? null) : s.currentChatId }
  }),

  selectChat: (id) => set({ currentChatId: id, showImageGen: false }),

  renameChat: (id, title) => set((s) => ({
    chats: s.chats.map((c) => (c.id === id ? { ...c, title } : c)),
  })),

  addMessage: (message) => set((s) => ({
    chats: s.chats.map((c) =>
      c.id === s.currentChatId
        ? { ...c, messages: [...c.messages, message], updatedAt: Date.now() }
        : c
    ),
  })),

  updateLastAssistantMessage: (content: string) => set((s) => ({
    chats: s.chats.map((c) => {
      if (c.id !== s.currentChatId) return c
      const msgs = [...c.messages]
      const last = msgs[msgs.length - 1]
      if (last && last.role === 'assistant') {
        msgs[msgs.length - 1] = { ...last, content }
      }
      return { ...c, messages: msgs, updatedAt: Date.now() }
    }),
  })),

  setChats: (chats) => set((s) => {
    // Keep current chat selection if it still exists
    const currentId = s.currentChatId
    const stillExists = currentId && chats.find((c) => c.id === currentId)
    return { chats, currentChatId: stillExists ? currentId : (chats[0]?.id ?? null) }
  }),
}))
